import CheckIconAlt from '@src/svgs/CheckIconAlt';
import { CLIP_REVIEW_STATUSES } from '@src/types.ts/video';
import styled from 'styled-components';

import { UNASSIGNED_FILTER_VALUE } from './useSocialClipsFilter';

import type { FilterState } from './useSocialClipsFilter';
import type { ClipReviewStatus } from '@src/types.ts/video';

type Props = {
  filters: FilterState;
  availableCreators: string[];
  availableAssignees: string[];
  /** Maps an assignee email to its display name; falls back to the email. */
  assigneeLabels: Record<string, string>;
  /** Gates the Status + Assignee filter sections (PLAT-10480). */
  showReviewWorkflow: boolean;
  /**
   * What the active tab holds — "Clip" or "Reel" — so the Status and Length
   * sections name what they actually filter (PLAT-11249). The filters
   * themselves are the same either way; only the noun is contextual.
   */
  kindNoun: 'Clip' | 'Reel';
  toggleClipStatusFilter: (status: 'published' | 'draft') => void;
  toggleClipLengthFilter: (
    length:
      | 'over240s'
      | 's120to240s'
      | 's60to120s'
      | 's30to60s'
      | 'lessThan30s',
  ) => void;
  toggleCreatorFilter: (creator: string) => void;
  toggleReviewStatusFilter: (status: ClipReviewStatus) => void;
  toggleAssigneeFilter: (assignee: string) => void;
};

export default function SocialClipsFilterDropdown({
  filters,
  availableCreators,
  availableAssignees,
  assigneeLabels,
  showReviewWorkflow,
  kindNoun,
  toggleClipStatusFilter,
  toggleClipLengthFilter,
  toggleCreatorFilter,
  toggleReviewStatusFilter,
  toggleAssigneeFilter,
}: Props) {
  return (
    <DropdownMenu data-testid="video-clips-filter-dropdown">
      {/* No Type section: the Clips / Reels tabs above the grid own that
          split (PLAT-11249, superseding PLAT-11128's filter) — a type filter
          on top of the tabs could only empty the current one. */}
      {showReviewWorkflow && (
        <>
          {/* Status */}
          <SectionLabel>Status</SectionLabel>
          {CLIP_REVIEW_STATUSES.map(status => (
            <FilterItem
              key={status}
              onClick={() => toggleReviewStatusFilter(status)}
            >
              <Checkbox $checked={filters.reviewStatus.has(status)}>
                {filters.reviewStatus.has(status) && <CheckIconAlt />}
              </Checkbox>
              <span>{status}</span>
            </FilterItem>
          ))}

          {/* Assignee */}
          {availableAssignees.length > 0 && (
            <>
              <SectionLabel>Assignee</SectionLabel>
              {availableAssignees.map(assignee => (
                <FilterItem
                  key={assignee || 'unassigned'}
                  onClick={() => toggleAssigneeFilter(assignee)}
                >
                  <Checkbox $checked={filters.assignees.has(assignee)}>
                    {filters.assignees.has(assignee) && <CheckIconAlt />}
                  </Checkbox>
                  <span>
                    {assignee === UNASSIGNED_FILTER_VALUE
                      ? 'Unassigned'
                      : assigneeLabels[assignee] || assignee}
                  </span>
                </FilterItem>
              ))}
            </>
          )}
        </>
      )}

      {/* Clip Status */}
      <SectionLabel>{kindNoun} Status</SectionLabel>
      <FilterItem onClick={() => toggleClipStatusFilter('published')}>
        <Checkbox $checked={filters.clipStatus.published}>
          {filters.clipStatus.published && <CheckIconAlt />}
        </Checkbox>
        <span>Published</span>
      </FilterItem>
      <FilterItem onClick={() => toggleClipStatusFilter('draft')}>
        <Checkbox $checked={filters.clipStatus.draft}>
          {filters.clipStatus.draft && <CheckIconAlt />}
        </Checkbox>
        <span>Draft</span>
      </FilterItem>

      {/* Clip Length */}
      <SectionLabel>{kindNoun} Length</SectionLabel>
      <FilterItem onClick={() => toggleClipLengthFilter('over240s')}>
        <Checkbox $checked={filters.clipLength.over240s}>
          {filters.clipLength.over240s && <CheckIconAlt />}
        </Checkbox>
        <span>&gt;240s</span>
      </FilterItem>
      <FilterItem onClick={() => toggleClipLengthFilter('s120to240s')}>
        <Checkbox $checked={filters.clipLength.s120to240s}>
          {filters.clipLength.s120to240s && <CheckIconAlt />}
        </Checkbox>
        <span>120s - 240s</span>
      </FilterItem>
      <FilterItem onClick={() => toggleClipLengthFilter('s60to120s')}>
        <Checkbox $checked={filters.clipLength.s60to120s}>
          {filters.clipLength.s60to120s && <CheckIconAlt />}
        </Checkbox>
        <span>60s - 120s</span>
      </FilterItem>
      <FilterItem onClick={() => toggleClipLengthFilter('s30to60s')}>
        <Checkbox $checked={filters.clipLength.s30to60s}>
          {filters.clipLength.s30to60s && <CheckIconAlt />}
        </Checkbox>
        <span>30s - 60s</span>
      </FilterItem>
      <FilterItem onClick={() => toggleClipLengthFilter('lessThan30s')}>
        <Checkbox $checked={filters.clipLength.lessThan30s}>
          {filters.clipLength.lessThan30s && <CheckIconAlt />}
        </Checkbox>
        <span>&lt;30s</span>
      </FilterItem>

      {/* Creator */}
      {availableCreators.length > 0 && (
        <>
          <SectionLabel>Creator</SectionLabel>
          {availableCreators.map(creator => (
            <FilterItem
              key={creator}
              onClick={() => toggleCreatorFilter(creator)}
            >
              <Checkbox $checked={filters.creators.has(creator)}>
                {filters.creators.has(creator) && <CheckIconAlt />}
              </Checkbox>
              <span>{creator}</span>
            </FilterItem>
          ))}
        </>
      )}
    </DropdownMenu>
  );
}

/* Rounded-row menu surface per docs/design-system.md "Dropdown / Popover
   Menus": small container padding + row gap, no divider lines. Mirrors the
   Home filters' DropdownMenu (RecentContentFilters). */
const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 180px;
  max-height: 320px;
  overflow-y: auto;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 4px;
  z-index: 1000;
  box-shadow: var(--elevation-toast);
`;

const SectionLabel = styled.div`
  flex-shrink: 0;
  padding: 8px 6px 2px;
  font-size: 11px;
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-muted);
  letter-spacing: 0.3px;
`;

const FilterItem = styled.button`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  width: 100%;
  min-height: 30px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 120ms ease;

  &:hover {
    background-color: var(--surface-elevated);
  }
`;

const Checkbox = styled.span<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid
    ${props => (props.$checked ? 'var(--accent)' : 'var(--border)')};
  background-color: ${props =>
    props.$checked ? 'var(--accent)' : 'transparent'};
  flex-shrink: 0;
  transition: all var(--transition-fast) ease;
`;
